import { createRouter, createWebHistory } from 'vue-router'
import { useAuth } from '../composables/useAuth'
import ChecksStartView from '../views/ChecksStartView.vue'
import ChecksProcessView from '../views/ChecksProcessView.vue'
import ChecksDamageView from '../views/ChecksDamageView.vue'
import ChecksServicesView from '../views/ChecksServicesView.vue'
import ChecksCompleteView from '../views/ChecksCompleteView.vue'
import DriveStartView from '../views/DriveStartView.vue'
import DriveActiveView from '../views/DriveActiveView.vue'

// Erweitere die RouteMeta-Schnittstelle
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresChecker?: boolean
    requiresDriver?: boolean
    requiresDisponent?: boolean
    requiresAdmin?: boolean
    requiresLocation?: boolean
    transition?: string
    depth?: number
    prevDepth?: number
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/dashboard',
      name: 'dashboard',
      // All roles use the mobile-optimized dashboard in the mobile app
      component: () => import('../views/DashboardView.vue'),
      meta: { requiresAuth: true, requiresLocation: true, transition: 'fade', depth: 0 }
    },
    {
      path: '/',
      redirect: '/dashboard'
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: { requiresAuth: false, transition: 'fade', depth: 0 }
    },
    {
      path: '/location-required',
      name: 'location-required',
      component: () => import('../views/LocationRequiredView.vue'),
      meta: { requiresAuth: false, transition: 'fade', depth: 0 }
    },
    {
      path: '/role-selection',
      name: 'role-selection',
      component: () => import('../views/RoleSelectionView.vue'),
      meta: { requiresAuth: true, transition: 'fade', depth: 0 }
    },
    {
      path: '/terminal-selection',
      name: 'terminal-selection',
      component: () => import('../views/TerminalSelectionView.vue'),
      meta: { requiresAuth: true, requiresChecker: true, requiresLocation: true, transition: 'fade', depth: 0 }
    },
    {
      path: '/checks/start',
      name: 'checks',
      component: ChecksStartView,
      meta: { requiresAuth: true, requiresChecker: true, requiresLocation: true, transition: 'slide-up', depth: 1 }
    },
    {
      path: '/checks/:id/process',
      name: 'checks-process',
      component: ChecksProcessView,
      meta: { requiresAuth: true, requiresLocation: true, transition: 'slide-up', depth: 2 }
    },
    {
      path: '/checks/:id/damage',
      name: 'checks-damage',
      component: ChecksDamageView,
      meta: { requiresAuth: true, requiresLocation: true, transition: 'slide-up', depth: 2 }
    },
    {
      path: '/checks/:id/services',
      name: 'checks-services',
      component: ChecksServicesView,
      meta: { requiresAuth: true, requiresLocation: true, transition: 'slide-up', depth: 2 }
    },
    {
      path: '/checks/:id/complete',
      name: 'checks-complete',
      component: ChecksCompleteView,
      meta: { requiresAuth: true, requiresLocation: true, transition: 'slide-down', depth: 2 }
    },
    {
      path: '/drives',
      name: 'drives',
      component: () => import('../views/DrivesView.vue'),
      meta: { requiresAuth: true, requiresDriver: true, requiresLocation: true, transition: 'slide-left', depth: 1 }
    },
    {
      path: '/drives/start',
      name: 'drive-start',
      component: DriveStartView,
      meta: { requiresAuth: true, requiresDriver: true, requiresLocation: true, transition: 'slide-up', depth: 2 }
    },
    {
      path: '/drives/:id',
      name: 'drive-active',
      component: DriveActiveView,
      meta: {
        requiresAuth: true,
        requiresDriver: true,
        requiresLocation: true,
        transition: 'slide-up',
        depth: 2
      },
      props: true
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/ProfileView.vue'),
      meta: { requiresAuth: true, requiresLocation: true, transition: 'slide-left', depth: 1 }
    },
    {
      path: '/driver-verification',
      name: 'driver-verification',
      component: () => import('../views/DriverVerificationView.vue'),
      meta: { requiresAuth: true, requiresDriver: true, requiresLocation: true, transition: 'fade', depth: 0 }
    },
    {
      path: '/tasks',
      name: 'tasks',
      component: () => import('../views/TasksView.vue'),
      meta: { requiresAuth: true, requiresDisponent: true, requiresLocation: true, transition: 'slide-left', depth: 1 }
    },
    {
      // DEV-135 (Standtage 15a): operational standing-vehicles view for dispatchers.
      path: '/standing-vehicles',
      name: 'standing-vehicles',
      component: () => import('../views/StandingVehiclesView.vue'),
      meta: { requiresAuth: true, requiresDisponent: true, transition: 'slide-left', depth: 1 }
    },
    {
      // TRK-16: Quick Checkout — Disponent übergibt per Scan + Ziel,
      // Buchung landet auf dem letzten Key.
      path: '/quick-checkout',
      name: 'quick-checkout',
      component: () => import('../views/QuickCheckoutView.vue'),
      meta: { requiresAuth: true, requiresDisponent: true, transition: 'slide-left', depth: 1 }
    },
    {
      path: '/key-handover',
      name: 'key-handover',
      component: () => import('../views/KeyHandoverView.vue'),
      meta: { requiresAuth: true, requiresDriver: true, requiresLocation: true, transition: 'slide-left', depth: 1 }
    },
    {
      path: '/key-handover/single',
      name: 'key-handover-single',
      component: () => import('../views/SingleKeyHandoverView.vue'),
      meta: { requiresAuth: true, requiresDriver: true, requiresLocation: true, transition: 'slide-up', depth: 2 }
    },
    {
      path: '/key-handover/list',
      name: 'key-handover-list',
      component: () => import('../views/KeyListView.vue'),
      meta: { requiresAuth: true, requiresDriver: true, requiresLocation: true, transition: 'slide-up', depth: 2 }
    },
    {
      path: '/key-handover/list/:id',
      name: 'key-handover-list-detail',
      component: () => import('../views/KeyListDetailView.vue'),
      meta: { requiresAuth: true, requiresDriver: true, requiresLocation: true, transition: 'slide-up', depth: 3 }
    },
    {
      path: '/key-handover/bulk/:id',
      name: 'key-handover-bulk',
      component: () => import('../views/BulkKeyHandoverView.vue'),
      meta: { requiresAuth: true, requiresDriver: true, requiresLocation: true, transition: 'slide-up', depth: 3 }
    },
    // New key management routes
    {
      path: '/keys',
      name: 'keys',
      component: () => import('../views/keys/KeyDashboard.vue'),
      meta: { requiresAuth: true, requiresLocation: true, transition: 'slide-left', depth: 1 }
    },
    {
      path: '/keys/ready',
      name: 'keys-ready',
      component: () => import('../views/keys/KeyDashboard.vue'),
      meta: { requiresAuth: true, requiresLocation: true, transition: 'slide-up', depth: 2 }
    },
    {
      path: '/keys/:id',
      name: 'key-details',
      component: () => import('../views/keys/KeyDetails.vue'),
      meta: { requiresAuth: true, requiresLocation: true, transition: 'slide-up', depth: 2 }
    },
    {
      path: '/keys/:id/handover',
      name: 'key-handover-single-new',
      component: () => import('../views/keys/KeyHandover.vue'),
      meta: { requiresAuth: true, requiresDriver: true, requiresLocation: true, transition: 'slide-up', depth: 3 }
    },
    {
      path: '/key-lists',
      name: 'key-lists',
      component: () => import('../views/KeyListView.vue'),
      meta: { requiresAuth: true, requiresLocation: true, transition: 'slide-left', depth: 1 }
    },
    {
      path: '/key-lists/create',
      name: 'key-list-create',
      component: () => import('../views/keys/KeyListCreate.vue'),
      meta: { requiresAuth: true, requiresLocation: true, transition: 'slide-up', depth: 2 }
    },
    {
      path: '/key-lists/:id',
      name: 'key-list-detail',
      component: () => import('../views/KeyListDetailView.vue'),
      meta: { requiresAuth: true, requiresLocation: true, transition: 'slide-up', depth: 2 }
    }
  ]
})

// Speichere die vorherige Route für Navigationsrichtung
let previousRoute = { name: '', meta: { depth: 0 } }

router.beforeEach(async (to, _from, next) => {
  console.log('Navigation to:', to.path)
  console.log('Route meta:', to.meta)
  
  // Setze die Navigationsrichtung basierend auf der Tiefe
  to.meta.prevDepth = previousRoute.meta.depth || 0
  
  const { state, isChecker, isDriver, isDisponent, fetchUser, hasSelectedTerminal, hasSelectedLocation, isDriverVerified } = useAuth()
  
  // SPEZIALFALL: Fahrer im Offline-Modus dürfen immer navigieren (keine Standortprüfung, kein Redirect)
  if (!navigator.onLine && localStorage.getItem('token')) {
    const userStr = localStorage.getItem('user')
    if (userStr) {
    try {
        const user = JSON.parse(userStr)
        state.value.user = user
        state.value.isAuthenticated = true
        // Key off the ACTIVE role (isDriver reads activeRole, restored from
        // localStorage even offline) — not the static primary role_name/role_id.
        if (isDriver.value) {
          console.log('Offline-Fahrer: Navigation ohne Standortprüfung erlaubt:', to.path)
          return next()
      }
      } catch (e) {
        console.warn('Offline-Modus: User konnte nicht aus LocalStorage geparst werden')
      }
    }
  }
  
  // Wenn der Benutzer nicht eingeloggt ist und die Route Auth erfordert
  if (to.meta.requiresAuth && !state.value.user) {
    // Offline-Workaround: Wenn offline und Token vorhanden, lasse zu
    if (!navigator.onLine && localStorage.getItem('token')) {
      console.log('Offline-Modus: Token vorhanden, Navigation trotzdem erlauben')
      // Setze User aus LocalStorage in den State, falls vorhanden
      const userStr = localStorage.getItem('user')
      if (userStr) {
        try {
          const user = JSON.parse(userStr)
          state.value.user = user
          state.value.isAuthenticated = true
          console.log('Offline-Modus: User aus LocalStorage in den State gesetzt:', user)
        } catch (e) {
          console.warn('Offline-Modus: User konnte nicht aus LocalStorage geparst werden')
        }
      }
      return next()
    }
    console.log('User not authenticated, trying to fetch user data')
    try {
      const userData = await fetchUser()
      console.log('User data fetched:', userData)
      if (!userData) {
        console.log('No user data found, redirecting to login')
        return next({ name: 'login' })
      }
    } catch (error) {
      console.error('Error fetching user data:', error)
      return next({ name: 'login' })
    }
  }

  // Überprüfe Rollen-spezifische Zugriffsrechte
  if (to.meta.requiresChecker && !isChecker.value && !isDisponent.value) {
    console.log('User is not a checker or disponent, redirecting to dashboard')
    return next({ name: 'dashboard' })
  }
  
  if (to.meta.requiresDriver && !isDriver.value) {
    console.log('User is not a driver, redirecting to dashboard')
    return next({ name: 'dashboard' })
  }

  if (to.meta.requiresDisponent && !isDisponent.value) {
    console.log('User is not a disponent, redirecting to dashboard')
    return next({ name: 'dashboard' })
  }

  // Location-Auswahl-Logik für Checker und Disponenten
  if ((isChecker.value || isDisponent.value) && to.name !== 'terminal-selection' && to.name !== 'login') {
    // Checker need terminal selection, Dispatchers need location selection (service center or terminal)
    const needsLocationSelection = isChecker.value ? !hasSelectedTerminal.value : !hasSelectedLocation.value
    
    if (needsLocationSelection) {
      console.log(`${isChecker.value ? 'Checker' : 'Dispatcher'} has no location selected, redirecting to terminal selection`)
      return next({ name: 'terminal-selection' })
    }
  }

  // Fahrer-Verifizierungs-Logik - für alle Routen außer Login und Verifizierung selbst
  if (isDriver.value && to.name !== 'driver-verification' && to.name !== 'login' && to.name !== 'location-required') {
    console.log('Driver verification check - isDriver:', isDriver.value, 'route:', to.name)
    
    // Prüfe, ob der Fahrer bereits verifiziert ist
    const verified = isDriverVerified()
    console.log('Driver verification status in router guard:', verified)
    
    if (!verified) {
      console.log('Driver not verified, navigating to verification')
      return next({ 
        name: 'driver-verification',
        query: { redirect: to.fullPath }
      })
    } else {
      console.log('Driver already verified, allowing navigation to:', to.name)
    }
  }

  // Location-Überprüfung für Routen, die Location erfordern
  if (to.meta.requiresLocation) {
    // Hier könnten wir eine Location-Überprüfung hinzufügen
    // Für jetzt lassen wir es durch, da die Location-Überprüfung in den Komponenten erfolgt
    console.log('Route requires location, but location check is handled in components')
  }

  // Wenn Benutzer zur Location-Auswahl navigiert, aber bereits eine Location hat
  if (to.name === 'terminal-selection') {
    const alreadyHasSelection = isChecker.value ? hasSelectedTerminal.value : hasSelectedLocation.value
    if (alreadyHasSelection) {
      console.log(`${isChecker.value ? 'Checker' : 'Dispatcher'} already has location selected, redirecting to dashboard`)
      return next({ name: 'dashboard' })
    }
  }
  
  // Speichere die aktuelle Route als vorherige für die nächste Navigation
  previousRoute = { name: to.name as string, meta: { depth: to.meta.depth || 0 } }
  
  next()
})

export default router 